import { emptyScanRecord } from '@odo/shared-ui';
import type { ScanRecord, ScanStorage } from '@odo/shared-ui';
import api, { AUTH_TOKEN_STORAGE_KEY } from '../api/config';
import { fetchFileAnnotations, putFileAnnotation } from './fileAnnotations';

/**
 * Backend-backed ScanStorage for the CBCT viewer: the scan record's four
 * sections live in storage.file_annotation, one row per kind, keyed by the
 * source scan's patientFileId (the viewer's scanId). Loads hydrate from the
 * API; patches are debounced per (scan, section) and PUT whole-section, so
 * markings roam across devices and admins instead of living in localStorage.
 *
 * The dashboard's viewer only ever calls `patch` for the `arch` section (the
 * viewer is read-only otherwise), but the write path stays generic — mirrors
 * files-viewer's store so the two never drift, and the backend enforces the
 * per-kind write authorization regardless of what the client attempts.
 */

const KIND_BY_SECTION = {
    arch: 'cbct.arch',
    tmj: 'cbct.tmj',
    measurements: 'cbct.measurements',
    annotations: 'cbct.annotations'
} as const;
type Section = keyof typeof KIND_BY_SECTION;
const SECTIONS = Object.keys(KIND_BY_SECTION) as Section[];

const PATCH_DEBOUNCE_MS = 800;

// Debounced edits wait in this module-level registry until their timer PUTs
// them. Unlike the old synchronous localStorage store, a write here can still
// be pending when the tab closes — so `pagehide`/tab-hide flushes everything
// pending with keepalive fetches (survive page teardown; sendBeacon can't
// carry the Authorization header).
interface PendingWrite {
    patientId: string;
    scanId: string;
    kind: string;
    payload: unknown;
}
const pendingWrites = new Map<string, PendingWrite>();
const timers = new Map<string, ReturnType<typeof setTimeout>>();

function flushPendingWrites(): void {
    timers.forEach((t) => clearTimeout(t));
    timers.clear();
    const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    pendingWrites.forEach((w) => {
        try {
            void fetch(
                `${api.defaults.baseURL}/patients/${w.patientId}/cloud-files/${w.scanId}/annotations/${w.kind}`,
                {
                    method: 'PUT',
                    keepalive: true,
                    headers: {
                        'Content-Type': 'application/json',
                        ...(token ? { Authorization: `Bearer ${token}` } : {})
                    },
                    body: JSON.stringify({ payload: w.payload })
                }
            );
        } catch {
            // Best-effort: keepalive bodies are capped (~64KB) and the page is
            // tearing down — nothing more can be done here.
        }
    });
    pendingWrites.clear();
}

let flushListenerRegistered = false;
function ensureFlushListener(): void {
    if (flushListenerRegistered || typeof window === 'undefined') return;
    flushListenerRegistered = true;
    window.addEventListener('pagehide', flushPendingWrites);
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') flushPendingWrites();
    });
}

const isPoint = (p: unknown): boolean =>
    Array.isArray(p) && p.length === 2 && typeof p[0] === 'number' && typeof p[1] === 'number';
const isLine = (l: unknown): boolean => Array.isArray(l) && l.every(isPoint);

/** Assemble a fully-defaulted ScanRecord from the per-kind API rows. */
function recordFromRows(rows: Array<{ kind: string; payload: unknown }>): ScanRecord {
    const byKind = new Map(rows.map((r) => [r.kind, r.payload]));
    const empty = emptyScanRecord();

    const arch = byKind.get(KIND_BY_SECTION.arch);
    const tmj = byKind.get(KIND_BY_SECTION.tmj) as { right?: unknown; left?: unknown } | undefined;
    const measurements = byKind.get(KIND_BY_SECTION.measurements);
    const annotations = byKind.get(KIND_BY_SECTION.annotations) as
        ScanRecord['annotations'] | undefined;

    return {
        arch: isLine(arch) ? (arch as ScanRecord['arch']) : empty.arch,
        tmj:
            tmj && isLine(tmj.right) && isLine(tmj.left)
                ? { right: tmj.right as ScanRecord['arch'], left: tmj.left as ScanRecord['arch'] }
                : empty.tmj,
        measurements: Array.isArray(measurements)
            ? (measurements as ScanRecord['measurements'])
            : empty.measurements,
        annotations:
            annotations && Array.isArray(annotations.annotations) ? annotations : empty.annotations
    };
}

export function createCbctScanStorage(patientId: string): ScanStorage {
    ensureFlushListener();

    // One trailing debounce per (scan, section) so a drag that fires many state
    // updates lands as a single PUT once the motion settles. The pending payload
    // is tracked so the page-teardown flush above can still deliver it.
    const schedulePut = (scanId: string, section: Section, payload: unknown): void => {
        const kind = KIND_BY_SECTION[section];
        const key = `${patientId}:${scanId}:${kind}`;
        const existing = timers.get(key);
        if (existing) clearTimeout(existing);
        pendingWrites.set(key, { patientId, scanId, kind, payload });
        timers.set(
            key,
            setTimeout(() => {
                timers.delete(key);
                pendingWrites.delete(key);
                putFileAnnotation(patientId, scanId, kind, payload).catch((err) => {
                    // Best-effort, like the localStorage store: a failed save must never
                    // break the viewer. Surface it for debugging instead of swallowing.
                    console.warn(`CBCT annotation save failed (${section}):`, err);
                });
            }, PATCH_DEBOUNCE_MS)
        );
    };

    return {
        async load(scanId) {
            if (!scanId) return emptyScanRecord();
            try {
                const rows = await fetchFileAnnotations(patientId, scanId);
                return recordFromRows(rows);
            } catch (err) {
                // Best-effort: an annotations outage must not block the scan from
                // opening — start from an empty record instead.
                console.warn('CBCT annotations load failed, starting empty:', err);
                return emptyScanRecord();
            }
        },

        patch(scanId, patch) {
            if (!scanId) return;
            SECTIONS.forEach((section) => {
                if (patch[section] !== undefined) schedulePut(scanId, section, patch[section]);
            });
        }
    };
}
